import api from './api';
import { endpoints } from './endpoints';
import { getMarketingTelegramBotUsername } from '../config/telegramMarketing';
import { resolveMediaUrl } from '../utils/resolveMediaUrl';
import {
  getEffectiveShopRootDomain,
  getMarketingShopPublicOrigin,
  isMarketingShopSubdomainUrlEnabled,
  MARKETING_SHOP_PATH_PREFIX,
} from '../utils/marketingShopHost';

const SETTINGS_KEY = 'adde_marketing_branch_settings_v1';

function slugify(input) {
  return String(input || '')
    .toLowerCase()
    .replace(/[^a-z0-9\u0400-\u04ff]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

export function buildBranchShopSlug(branch) {
  const base = slugify(branch?.name || 'filial');
  const id = String(branch?.id || '').trim();
  return id ? `${base}-${id}` : base || 'filial';
}

/** Do‘kon URL segmenti: server va frontend bir xil qoida (harf, raqam, tire). */
function normalizeShopUrlSegment(seg) {
  if (seg == null) return '';
  return String(seg)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);
}

export function branchIdFromSlug(slug) {
  const parts = String(slug || '').split('-').filter(Boolean);
  return parts.length ? parts[parts.length - 1] : '';
}

/** Slug to‘liq mos keladigan filial ID (buildBranchShopSlug bilan); bo‘lmasa oxirgi segment. */
export function findBranchIdByShopSlug(slug, branches) {
  if (!slug || !Array.isArray(branches)) return branchIdFromSlug(slug);
  const s = String(slug).trim();
  const match =
    branches.find((b) => buildBranchShopSlug(b) === s) ||
    branches.find((b) => String(b.slug ?? '').trim() === s) ||
    branches.find(
      (b) =>
        String(b.shopSubdomain ?? b.shop_subdomain ?? '')
          .trim()
          .toLowerCase() === s.toLowerCase(),
    );
  return match?.id != null ? String(match.id) : branchIdFromSlug(slug);
}

/**
 * Mijozga ko‘rinadigan do‘kon kirish havolasi.
 * Default: qisqa marshrut `/{MARKETING_SHOP_PATH_PREFIX}/{segment}` (segment: shop_subdomain → slug → id → nom-id).
 * Subdomen: faqat `VITE_SHOP_USE_SUBDOMAIN=true` + wildcard SSL.
 * @param {Record<string, unknown>} branch — normalizeBranch yoki API qatori
 * @param {string} [origin] — zaxira: getMarketingShopPublicOrigin() bo‘sh bo‘lsa
 */
export function buildPublicShopEntryUrl(branch, origin) {
  const root = getEffectiveShopRootDomain();
  const protocol =
    typeof window !== 'undefined' && window.location?.protocol === 'http:' ? 'http' : 'https';
  /** Laravel {@see Branch::publicShopPathSegment()} — mijoz vitrinasi bilan bir xil */
  const serverSeg = normalizeShopUrlSegment(branch?.shopPathSegment ?? branch?.shop_path_segment ?? '');
  const explicit = normalizeShopUrlSegment(branch?.shopSubdomain ?? branch?.shop_subdomain ?? '');
  const slugFromApi =
    branch?.slug != null && String(branch.slug).trim() !== ''
      ? normalizeShopUrlSegment(branch.slug)
      : '';
  const fallbackSlug = normalizeShopUrlSegment(buildBranchShopSlug(branch));
  const idStr =
    branch?.id != null && String(branch.id).trim() !== ''
      ? normalizeShopUrlSegment(String(branch.id))
      : '';
  /** Server → subdomen → slug → nom-id → id (server maydoni bo‘lmasa, eski mijozlar uchun) */
  const pathSegment = serverSeg || explicit || slugFromApi || fallbackSlug || idStr;

  if (isMarketingShopSubdomainUrlEnabled() && root && pathSegment) {
    return `${protocol}://${pathSegment}.${root}/`;
  }

  const base =
    getMarketingShopPublicOrigin() ||
    origin ||
    (typeof window !== 'undefined' ? window.location.origin : '');
  const enc = /^[A-Za-z0-9._~-]+$/.test(pathSegment) ? pathSegment : encodeURIComponent(pathSegment);
  return `${String(base).replace(/\/+$/, '')}/${MARKETING_SHOP_PATH_PREFIX}/${enc}`;
}

/**
 * Dastafka va boshqa marketing sozlamalari (Telegram ID yubormaymiz — serverdagi qiymat saqlanadi).
 */
export async function updateMarketingBranchSettings(branchId, { deliveryEnabled } = {}) {
  if (!branchId) return null;
  const body = {};
  if (typeof deliveryEnabled === 'boolean') {
    body.delivery_enabled = deliveryEnabled;
  }
  if (Object.keys(body).length === 0) return null;
  await api.put(endpoints.marketingBranchSettings(branchId), body);
  return true;
}

export function loadBranchMarketingSettings() {
  try {
    if (typeof window === 'undefined') return {};
    const raw = window.localStorage.getItem(SETTINGS_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function saveBranchMarketingSettings(next) {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(next || {}));
  } catch {}
}

export function getBranchMarketingSetting(branchId) {
  const all = loadBranchMarketingSettings();
  const key = String(branchId || '');
  const item = all[key] || {};
  return {
    deliveryEnabled: Boolean(item.deliveryEnabled),
    telegramGroupId: String(item.telegramGroupId || ''),
    telegramGroupTitle: String(item.telegramGroupTitle || ''),
  };
}

export function setBranchMarketingSetting(branchId, patch) {
  const key = String(branchId || '');
  const all = loadBranchMarketingSettings();
  const prev = all[key] || {};
  all[key] = {
    ...prev,
    ...patch,
  };
  saveBranchMarketingSettings(all);
  return all[key];
}

export function getTelegramBotAddToGroupUrl(branchId) {
  const botUsername = getMarketingTelegramBotUsername();
  const payload = `branch_${String(branchId || '').trim() || 'unknown'}`;
  return `https://t.me/${botUsername}?startgroup=${encodeURIComponent(payload)}`;
}

/**
 * Tugma bosilganda Telegram ochiladi — foydalanuvchi guruhni tanlaydi, bot guruhga qo‘shiladi.
 * Faqat rasmiy https://t.me/...?startgroup=... (botni tanlash / chat ochish shart emas).
 */
export function openTelegramAddBotToGroup(branchId) {
  if (typeof window === 'undefined') return;
  const url = getTelegramBotAddToGroupUrl(branchId);
  if (!url) return;
  /** Faqat bitta yangi tab: `location.href` fallback ochiq Telegram / brauzerda ikkinchi sahifaga olib kelardi. */
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Laravel: { data: { ... } } yoki bir necha marta ichma-ich data.
 */
function unwrapTelegramPayload(res) {
  let x = res?.data ?? res;
  if (x == null || typeof x !== 'object') return null;
  for (let i = 0; i < 5; i++) {
    if (x && typeof x === 'object' && !Array.isArray(x) && x.data != null && typeof x.data === 'object' && !Array.isArray(x.data)) {
      x = x.data;
    } else {
      break;
    }
  }
  return x && typeof x === 'object' ? x : null;
}

const TG_ID_KEYS = [
  'telegram_group_id',
  'telegramGroupId',
  'telegram_chat_id',
  'telegramChatId',
  'group_chat_id',
  'chat_id',
  'group_id',
  'telegramGroupChatId',
  'supergroup_id',
];

const TG_TITLE_KEYS = [
  'telegram_group_title',
  'telegramGroupTitle',
  'group_title',
  'chat_title',
  'telegram_chat_title',
  'group_name',
];

function looksLikeTelegramChatId(value) {
  if (value == null) return false;
  const s = String(value).trim();
  if (!s || s === '0') return false;
  const n = /^-?\d+$/.test(s);
  return n && (s.startsWith('-100') || s.length >= 8);
}

/**
 * JSON daraxtida guruh chat_id / title qidirish (branch.telegram, meta, settings).
 */
function deepExtractTelegramChat(obj, depth = 0) {
  if (!obj || typeof obj !== 'object' || depth > 12) return { id: '', title: '', linkedAt: null, linkedFlag: false };

  let id = '';
  let title = '';
  let linkedAt = null;
  let linkedFlag = false;

  for (const k of TG_ID_KEYS) {
    if (obj[k] != null && String(obj[k]).trim() !== '') {
      const v = String(obj[k]).trim();
      if (looksLikeTelegramChatId(v) || (v !== '0' && v !== 'null' && /^-?\d+$/.test(v))) {
        id = v;
        break;
      }
    }
  }

  for (const k of TG_TITLE_KEYS) {
    if (obj[k] != null && String(obj[k]).trim() !== '') {
      title = String(obj[k]).trim();
      break;
    }
  }

  for (const k of ['linked_at', 'linkedAt', 'telegram_linked_at']) {
    if (obj[k] != null && String(obj[k]).trim() !== '') {
      linkedAt = obj[k];
      linkedFlag = true;
      break;
    }
  }

  linkedFlag =
    linkedFlag ||
    Boolean(
      obj.telegram_linked ??
        obj.is_telegram_linked ??
        obj.telegram_connected ??
        obj.telegram_linked_to_group ??
        obj.linked ??
        (obj.telegram_status === 'linked' || obj.telegram_status === 'active'),
    );

  if (id || title) {
    return { id, title, linkedAt, linkedFlag };
  }

  for (const key of Object.keys(obj)) {
    const v = obj[key];
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const nested = deepExtractTelegramChat(v, depth + 1);
      if (nested.id || nested.title) {
        return {
          id: nested.id,
          title: nested.title || title,
          linkedAt: nested.linkedAt ?? linkedAt,
          linkedFlag: nested.linkedFlag || linkedFlag,
        };
      }
    }
  }

  return { id: '', title: '', linkedAt, linkedFlag };
}

/**
 * Backend webhook botni guruhga qo‘shganda guruh ID ni saqlaydi — UI shu endpointdan o‘qiydi.
 * Turli maydon nomlari: telegram_group_id, telegram_chat_id, chat_id (supergroup -100...).
 */
export async function fetchBranchTelegramLink(branchId) {
  if (!branchId) return null;
  try {
    const res = await api.get(endpoints.marketingBranchTelegram(branchId));
    const inner = unwrapTelegramPayload(res);
    if (!inner || typeof inner !== 'object') return null;

    const extracted = deepExtractTelegramChat(inner);

    let telegramGroupId = extracted.id || '';
    if (telegramGroupId === '0' || telegramGroupId === 'null' || telegramGroupId === 'undefined') {
      telegramGroupId = '';
    }

    const telegramGroupTitle = extracted.title || '';

    let linkedFlag = extracted.linkedFlag;

    if (!telegramGroupId && linkedFlag && (telegramGroupTitle || extracted.linkedAt)) {
      telegramGroupId = '__linked__';
    }

    return {
      telegramGroupId,
      telegramGroupTitle,
      linkedAt: extracted.linkedAt ?? inner.linked_at ?? inner.linkedAt ?? null,
      linkedFlag,
    };
  } catch {
    return null;
  }
}

/**
 * Vitrina / public mahsulot qatorida o‘lchov-variantlar (sale_options) turli nom va formatda kelishi mumkin.
 */
function pickPublicSaleOptionsArray(p) {
  if (!p || typeof p !== 'object') return [];
  const candidates = [
    p.saleChoices,
    p.sale_choices,
    p.sale_options,
    p.saleOptions,
  ];
  for (const v of candidates) {
    if (Array.isArray(v)) return v;
    if (typeof v === 'string' && v.trim()) {
      try {
        const parsed = JSON.parse(v);
        if (Array.isArray(parsed)) return parsed;
      } catch {
        /* ignore */
      }
    }
  }
  return [];
}

function mapPublicProductRow(p) {
  const marketingTitle = String(
    p.marketing_title ??
      p.marketingTitle ??
      p.shop_title ??
      p.shopTitle ??
      p.showcase_title ??
      p.vitrina_title ??
      p.telegram_caption ??
      p.short_description ??
      '',
  ).trim();
  const marketingKeywords = String(
    p.marketing_keywords ?? p.marketingKeywords ?? p.seo_keywords ?? p.tags ?? p.keywords ?? '',
  ).trim();
  const saleOptionsRows = pickPublicSaleOptionsArray(p);
  const saleUnitRaw = String(p.sale_unit ?? p.saleUnit ?? 'piece').trim() || 'piece';
  return {
    id: String(p.id ?? p.product_id ?? ''),
    name: String(p.name ?? p.product_name ?? ''),
    sale_price: p.sale_price ?? p.salePrice ?? p.price ?? 0,
    salePrice: Number(p.sale_price ?? p.salePrice ?? p.price ?? 0),
    sale_unit: saleUnitRaw,
    saleUnit: saleUnitRaw,
    quantity: Number(p.quantity ?? p.qty ?? p.stock ?? 0),
    quantity_remaining: Number(p.quantity_remaining ?? p.quantity ?? p.qty ?? p.stock ?? 0),
    imageUrl: resolveMediaUrl(
      p.image_url ?? p.imageUrl ?? p.image ?? p.photo ?? p.photo_url ?? p.thumbnail_url ?? p.thumbnail ?? '',
    ),
    barcode: String(p.barcode ?? ''),
    marketingTitle,
    marketingKeywords,
    saleChoices: saleOptionsRows,
    sale_options: saleOptionsRows,
  };
}

function extractRowsAndMeta(res) {
  if (res == null || typeof res !== 'object') return { list: [], meta: null };
  if (Array.isArray(res.data)) {
    return { list: res.data, meta: res.meta && typeof res.meta === 'object' ? res.meta : null };
  }
  const raw = res.data ?? res;
  const list = raw?.data ?? raw?.items ?? (Array.isArray(raw) ? raw : []);
  const meta = raw?.meta && typeof raw.meta === 'object' ? raw.meta : res.meta ?? null;
  return { list: Array.isArray(list) ? list : [], meta };
}

/**
 * @param {unknown} res — api.get to‘g‘ridan Laravel JSON: { data: [...], meta? } (ichma-ich emas)
 * @returns {{ products: object[], meta: object | null }}
 */
function parsePublicProductsPayload(res) {
  if (res == null || typeof res !== 'object' || Array.isArray(res)) {
    return { products: [], meta: null };
  }
  /** fetch javobi: res.data allaqachon mahsulotlar massivi */
  let rows = [];
  if (Array.isArray(res.data)) {
    rows = res.data;
  } else if (res.data != null && typeof res.data === 'object' && !Array.isArray(res.data)) {
    const inner = res.data;
    rows = Array.isArray(inner.data) ? inner.data : Array.isArray(inner.items) ? inner.items : [];
  } else if (Array.isArray(res.items)) {
    rows = res.items;
  } else if (Array.isArray(res)) {
    rows = res;
  }
  const metaRaw = res.meta;
  const meta =
    metaRaw != null && typeof metaRaw === 'object' && !Array.isArray(metaRaw) ? metaRaw : null;
  const products = rows.map(mapPublicProductRow).filter((x) => x.id);
  return { products, meta };
}

/**
 * Filial vitrina mahsulotlari — avvalo maxsus public/marketing endpointlar,
 * keyin GET /api/products?branch_id= (skipAuth), oxirida JWT bilan (masalan, admin sinov).
 * @param {{ slug?: string }} [opts]
 * @returns {Promise<{ products: object[], meta: object | null }>}
 */
export async function getPublicBranchProducts(branchId, opts = {}) {
  const slug = String(opts.slug || '').trim();
  const idStr = String(branchId ?? '').trim();
  if (!slug && !idStr) return { products: [], meta: null };

  let lastMeta = null;

  const tryGet = async (path, options) => {
    const res = await api.get(path, options);
    return parsePublicProductsPayload(res);
  };

  if (slug) {
    try {
      const { products, meta } = await tryGet(endpoints.publicBranchShopProductsBySlug(slug), { skipAuth: true });
      /** 200 + bo'sh ro'yxat ham to'g'ri javob — aks holda `chust` kabi slug oxirgi segment sifatida noto'g'ri branch_id ga ketardi */
      return { products, meta };
    } catch {
      /* 404 / tarmoq — slug bo'yicha ochilmadi, quyidagi fallback */
    }
  }

  if (!idStr) return { products: [], meta: lastMeta };

  try {
    const { products, meta } = await tryGet(endpoints.marketingBranchShopProducts(branchId), { skipAuth: true });
    if (products.length) return { products, meta };
  } catch {}

  try {
    const { products, meta } = await tryGet(endpoints.products, {
      params: { branch_id: branchId, active_only: 1 },
      skipAuth: true,
    });
    if (products.length) return { products, meta };
  } catch {}

  try {
    const parsed = await tryGet(endpoints.products, {
      params: { branch_id: idStr, active_only: 1 },
    });
    return { products: parsed.products, meta: parsed.meta ?? lastMeta };
  } catch {
    return { products: [], meta: null };
  }
}

